import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, User } from 'lucide-react';

interface LeadershipMember {
  id: string;
  name: string;
  position: string;
  department: string;
  level: 'executive' | 'senior' | 'middle';
  description: string;
}

const Leadership = () => {
  const { t } = useLanguage();

  const leadershipTeam: LeadershipMember[] = [
    {
      id: '1',
      name: 'Ing. Peter Novák',
      position: 'Generálny riaditeľ',
      department: 'Vedenie spoločnosti',
      level: 'executive',
      description: 'Viac ako 20 rokov skúseností v automobilovom priemysle. Vedie spoločnosť k inováciám a udržateľnému rastu.'
    },
    {
      id: '2',
      name: 'Ing. Mária Kováčová',
      position: 'Finančná riaditeľka',
      department: 'Financie',
      level: 'executive',
      description: 'Zodpovedná za finančnú stratégiu a ekonomický rozvoj spoločnosti.'
    },
    {
      id: '3',
      name: 'Ing. Ján Mihálik',
      position: 'Prevádzkový riaditeľ',
      department: 'Výroba',
      level: 'executive',
      description: 'Riadi výrobné procesy a zabezpečuje efektivitu prevádzky.'
    },
    {
      id: '4',
      name: 'Ing. Zuzana Horváthová',
      position: 'Riaditeľka kvality',
      department: 'Kvalita',
      level: 'senior',
      description: 'Zodpovedná za systém riadenia kvality a certifikácie ISO.'
    },
    {
      id: '5',
      name: 'Ing. Tomáš Baláž',
      position: 'Riaditeľ logistiky',
      department: 'Logistika',
      level: 'senior',
      description: 'Koordinuje logistické procesy a dodávateľské reťazce.'
    },
    {
      id: '6',
      name: 'Ing. Lucia Šoltésová',
      position: 'HR riaditeľka',
      department: 'Ľudské zdroje',
      level: 'senior',
      description: 'Stará sa o rozvoj ľudských zdrojov a firemnej kultúry.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section id="leadership" className="py-20 bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%239C92AC\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-slate-800 via-slate-600 to-slate-800 bg-clip-text text-transparent mb-6"
          >
            {t('leadership.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed"
          >
            {t('leadership.description')}
          </motion.p>
        </motion.div>

        {/* Organizational Structure Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white/80 backdrop-blur-sm border border-slate-200/80 rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="text-center">
              <img 
                src="/ORG.png" 
                alt="Organizačná štruktúra IAC Group Slovakia"
                className="max-w-4xl mx-auto rounded-lg shadow-lg border border-slate-200"
              />
            </div>
          </div>
        </motion.div>

        
      </div>
    </section>
  );
};

export default Leadership;